import axios from "axios";
import { API_CONFIG, checkMixedContent } from "../config/security";
import { apiErrorBus } from "./apiErrorBus";
import { clearResourceCache } from "../hooks/useResource";

// Check for mixed content issues
checkMixedContent();

// Configure base URL for API calls - Always use HTTPS
const API_BASE_URL = API_CONFIG.baseURL;

const instance = axios.create({
  ...API_CONFIG,
  baseURL: API_BASE_URL,
});

// Add request interceptor to include auth token
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// A 401 from these is a wrong code or password, not an expired session.
const SIGN_IN_ENDPOINTS = ['/api/auth/send-otp', '/api/auth/verify-otp', '/api/auth/login'];

export const onResponseError = (error) => {
    // Extract proper error message instead of logging the whole error object
    let errorMessage = 'An error occurred';

    if (error.response) {
      // Server responded with error status
      errorMessage = error.response.data?.message ||
                     error.response.data?.error ||
                     `Server error: ${error.response.status} ${error.response.statusText}`;

      console.error(`API Response error [${error.response.status}]:`, errorMessage);

      if (error.response.status === 401 && !SIGN_IN_ENDPOINTS.includes(error.config?.url)) {
        // Unauthorized - clear token and redirect to login
        apiErrorBus.silenceForSignOut();
        localStorage.removeItem("token");
        clearResourceCache();
        window.location.href = '/login';
        return Promise.reject(new Error('Unauthorized - Please log in again'));
      } else if (error.response.status === 403) {
        errorMessage = 'Access denied: Insufficient permissions';
        console.error('Permission denied');
      } else if (error.response.status === 404) {
        errorMessage = error.response.data?.message || 'Resource not found';
        console.error('Resource not found:', error.config?.url);
      } else if (error.response.status >= 500) {
        errorMessage = 'Server error: Please try again later';
        console.error('Server error:', errorMessage);
        // Emit server error to global bus for toast notification
        apiErrorBus.emit(errorMessage);
      }
    } else if (error.request) {
      // Request made but no response received
      errorMessage = 'Network error: Unable to connect to server. Please check your internet connection.';
      console.error('Network error - no response received');
    } else {
      // Error in request setup
      errorMessage = error.message || 'An unexpected error occurred';
      console.error('Request setup error:', errorMessage);
    }

    // Create a new error with the proper message
    const apiError = new Error(errorMessage);
    apiError.response = error.response;
    apiError.request = error.request;
    apiError.config = error.config;

    return Promise.reject(apiError);
};

instance.interceptors.response.use((response) => response, onResponseError);

export default instance;
